import { selx } from "clsx";
import { jsx } from "panel";
import moment from "moment";
import { ExperimentalModalEl } from "common/components/modal";
import { CustomAlertEl } from "common/widgets/alerts/custom-alert";
import { ButtonBarTheme } from "common/components/button-bar/types";
import { ButtonEl } from "common/components/button";
import { ButtonSize, ButtonTheme } from "common/components/button/constants";
import { InputEl } from "common/components/input";
import { LastUpdatedEl } from "common/components/last-updated";
import { getReportIcon, PlaceholderCopy, SaveDashboardCopy } from "common/report/util/boards";
import { ControlBarEl } from "common/widgets/control-bar";
import { SharingStatusButtonEl } from "common/widgets/sharing/status-button";
import { getPublicDashboardTextHighlight } from "common/templates/public-dashboards/mixins";
import { BoardsSaveButtonEl } from "common/query-builder/query-report-header/boards-save-menu";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { ConfirmEl } from "common/components/confirm";
import { TableEl } from "common/components/table";
import { THEME as confirmTheme } from "common/components/confirm/types";
import { SvgIconEl } from "common/components/svg-icon";
import { ClampedTextEl } from "common/components/clamped-text";
import { SearchDisplayContext } from "common/report/util/search/types";
import { BoardsDashboardSelector } from "common/widgets/dashboard-selector/presets";
import { ButtonBarEl } from "common/components/button-bar";
import { Directions } from "common/components/drop-menu/types";
import { SIZE, TextHighlightEl, THEME } from "common/components/text-highlight";
import { TooltipEl } from "common/components/tooltip";
import { Placement } from "common/components/tooltip/types";
import { KEY_VALUES } from "common/util/dom";
import { truncate } from "common/util/string";
import { MAX_REPORT_DESCRIPTION_LENGTH } from "common/constants";
import { CreatorWithViewsIcon } from "common/widgets/user-list/presets";
import { SwitchEl } from "common/components/switch";
import { TooltipTourEl } from "common/components/tooltip-tour";
import { TourName } from "common/components/tooltip-tour/types";
import { userColumnProps } from "common/widgets/table-column/user";
import { BreadcrumbsEl, BreadcrumbsTheme } from "common/components/breadcrumbs";
import { getReportUrl } from "common/report/util";
import { HeaderAction, REPORT_HEADER_HEIGHT, SaveModes } from "./types";
import styles from "./index.module.styl";
export function AlertModal({ cmp }) {
  const displayContext = cmp.params.displayContext;
  const isReportValid = [SearchDisplayContext.Insights, SearchDisplayContext.Funnels].includes(displayContext);
  if (cmp.state.isCreateAlertModalOpen && isReportValid) {
    const resultsKeysAndValues = cmp.params.resultsKeysAndValues;
    const isPercentageThreshold = (resultsKeysAndValues == null ? void 0 : resultsKeysAndValues.length) && resultsKeysAndValues[resultsKeysAndValues.length - 1].isPercentage === true;
    return /* @__PURE__ */ jsx(ExperimentalModalEl, {
      sel: selx(styles.alertModal),
      attrs: {
        open: true,
        closeable: true,
        "grow-to-content": true,
        "hide-close-button": true
      },
      on: {
        change: (ev) => ev.detail.state === `closed` && cmp.helpers.closeCustomAlertModal()
      }
    }, /* @__PURE__ */ jsx(CustomAlertEl, {
      attrs: {
        "bookmark-id": cmp.params.selectedEntityId,
        "enable-condition-types": cmp.params.enableCustomAlertConditionTypes,
        "is-editing-mode": false,
        "is-percentage-threshold": isPercentageThreshold,
        "project-id": cmp.params.projectId,
        "report-type": displayContext,
        "results-keys-and-values": cmp.params.resultsKeysAndValues,
        "time-unit": cmp.params.customAlertTimeUnit,
        "date-range-interval-days": cmp.params.customAlertIntervalDays,
        "is-anomaly-detection-control-disabled": !cmp.params.enableAnomalyDetectionControls,
        "bookmark-params": cmp.params.bookmarkParams
      },
      on: {
        cancel: cmp.helpers.closeCustomAlertModal,
        create: cmp.helpers.handleCustomAlertCreated,
        error: cmp.helpers.handleCustomAlertError
      }
    }));
  }
  return null;
}
const AddDescriptionButton = ({ description, on, canEdit }) => {
  return /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryReportHeaderBoardsEditDescriptionButton, styles.editableDiv),
    attrs: { title: description, elref: `boardsDescription` },
    class: { [styles.canEdit]: canEdit },
    on: { ...on }
  }, description || `+ Add description...`);
};
const ReportInput = ({ value, onInputChanged, onEditStop }) => {
  return /* @__PURE__ */ jsx(InputEl, {
    attrs: {
      "select-on-focus": true,
      elref: `boardsHeaderInput`
    },
    props: {
      value
    },
    on: {
      blur: (ev) => onInputChanged == null ? void 0 : onInputChanged(ev.target.value),
      keydown: (ev) => {
        if (ev.key === KEY_VALUES.ESCAPE) {
          ev.preventDefault();
          onEditStop == null ? void 0 : onEditStop();
        } else if (ev.key === KEY_VALUES.ENTER) {
          ev.preventDefault();
          onInputChanged == null ? void 0 : onInputChanged(ev.target.value);
        }
      }
    },
    hook: {
      insert: (vnode) => {
        requestAnimationFrame(() => {
          vnode.elm.focus();
        });
      }
    }
  });
};
const EditDescriptionInput = ({ cmp }) => {
  return /* @__PURE__ */ jsx(ReportInput, {
    value: cmp.helpers.getReportDescription(),
    onInputChanged: (value) => {
      const prevDescription = cmp.helpers.getReportDescription();
      const truncatedDescription = value.substring(0, MAX_REPORT_DESCRIPTION_LENGTH);
      cmp.update({ isEditingDescription: false, newReportDescription: truncatedDescription });
      if (!cmp.hasSelectedEntity) {
        cmp.dispatchEvent(
          new CustomEvent(`editreport`, {
            detail: { description: truncatedDescription }
          })
        );
      }
      if (prevDescription !== cmp.state.newReportDescription) {
        cmp.autoSaveNameAndDescription();
      }
    },
    onEditStop: () => cmp.update({ isEditingDescription: false })
  });
};
const BreadcrumbsTitle = ({
  dashboardsRoute,
  id,
  isEditingTitle,
  title,
  projectId,
  workspaceId,
  canEdit,
  description,
  isViewOnly,
  duplicateReportUrl,
  onTitleChanged,
  onDescriptionClicked,
  onTitleClicked
}) => {
  const linkNodes = dashboardsRoute.map((board) => ({
    label: board.title,
    value: null,
    url: getReportUrl(`boards#id=${board.id}`, { projectId, workspaceId })
  }));
  linkNodes.push({ label: title, value: id, isInEditMode: isEditingTitle });
  return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(BreadcrumbsEl, {
    attrs: { theme: BreadcrumbsTheme.Path, editable: canEdit },
    props: { linkNodes },
    on: {
      nodeClick: (ev) => {
        const linkNode = ev.detail.node;
        if (linkNode.value === id) {
          onTitleClicked == null ? void 0 : onTitleClicked();
        }
      },
      nodeUpdated: (ev) => {
        const newNode = ev.detail.node;
        if (newNode.value === id) {
          onTitleChanged == null ? void 0 : onTitleChanged(newNode.label);
        }
      }
    }
  }), canEdit && /* @__PURE__ */ jsx(AddDescriptionButton, {
    description,
    on: {
      click: () => onDescriptionClicked == null ? void 0 : onDescriptionClicked()
    },
    canEdit: true
  }), isViewOnly ? /* @__PURE__ */ jsx(TextHighlightEl, {
    attrs: {
      elref: `viewOnlyHighlight`,
      markdown: `You have view only access. [Duplicate to make a copy](${duplicateReportUrl}).`,
      theme: THEME.INFORMATION
    }
  }) : null);
};
const BoardsTitle = ({ cmp }) => {
  var _a;
  const canEdit = cmp.params.canWrite && !cmp.hasSelectedEntity || cmp.canEditExistingEntity();
  const samplingNotice = cmp.helpers.getSamplingNotice({ isReportModeEnabled: true });
  const reportHasLinks = ((_a = cmp.extraDashboards) == null ? void 0 : _a.length) > 0;
  return /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryReportHeaderBoardsTitle)
  }, !cmp.helpers.isEditingReportNameOrDescription() ? /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryReportHeaderBoardsTitleText)
  }, cmp.showAvailableDashboardsDropdown() && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryReportHeaderDashboardActionMenu)
  }, /* @__PURE__ */ jsx(SelectEl, {
    sel: selx(styles.mpQueryReportHeaderBoardsTitleDashboard),
    attrs: {
      "always-show-caret": true,
      elref: `boardsDashboardDropdown`,
      theme: SelectTheme.Tertiary,
      tabIndex: 0,
      sections: cmp.getBoardsDashboardsSections(),
      size: ButtonSize.Small,
      "menu-width": 320,
      open: cmp.state.dashboardsDropdownOpen
    },
    on: {
      change: () => cmp.update({ dashboardSelectorOpen: false }),
      blur: () => cmp.update({ dashboardsDropdownOpen: false })
    }
  }, cmp.hasSelectedEntity && /* @__PURE__ */ jsx(ButtonBarEl, {
    attrs: {
      slot: `footer`,
      theme: ButtonBarTheme.Secondary,
      elref: `boardsDashboardDropdownLinkBtn`
    },
    on: { click: cmp.helpers.handleAddToAnotherDashboard }
  }, "Add to Another Board")), cmp.state.dashboardSelectorOpen && /* @__PURE__ */ jsx(BoardsDashboardSelector, {
    recentDashboardIds: cmp.getRecentlyViewedDashboards(),
    placeholder: PlaceholderCopy.DuplicateReport,
    quickCreateSaveDashboardCopy: SaveDashboardCopy.DuplicateReport,
    dashboardController: cmp.dashboardController,
    handleMenuChanged: (ev) => ev.detail.state !== `open` ? cmp.update({ dashboardSelectorOpen: false }) : null,
    handleDashboardSelected: cmp.helpers.handleLinkDashboard,
    direction: Directions.Down,
    offset: -20,
    excludedDashboards: cmp.helpers.getDashboardSelectorExcludedDashboards()
  })), /* @__PURE__ */ jsx("div", null, "/"), /* @__PURE__ */ jsx("div", {
    sel: selx(styles.reportIcon)
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    sel: selx(styles.reportIconSvg),
    attrs: {
      elref: `reportIconSvg`,
      icon: getReportIcon(cmp.params.displayContext, reportHasLinks)
    }
  }), reportHasLinks && /* @__PURE__ */ jsx(TooltipEl, {
    attrs: {
      text: `This report is linked to other copies.
Changes made to this report will be
 reflected across all other copies.`
    }
  }))), /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryReportHeaderBoardsTitleBookmark, styles.editableDiv),
    class: { [styles.canEdit]: canEdit, [styles.disabledColor]: !canEdit },
    attrs: { title: cmp.helpers.getReportName(), elref: `boardsTitle` },
    on: { click: () => canEdit && cmp.helpers.handleEditReportName() }
  }, cmp.helpers.getReportName()), samplingNotice ? /* @__PURE__ */ jsx("span", null, /* @__PURE__ */ jsx(TextHighlightEl, {
    sel: selx(styles.mpSamplingNoticeText),
    attrs: {
      elref: `samplingNotice`,
      markdown: `Sampled`,
      theme: THEME.INFORMATION
    }
  }), /* @__PURE__ */ jsx(TooltipEl, {
    attrs: { text: samplingNotice }
  })) : null, canEdit && /* @__PURE__ */ jsx(AddDescriptionButton, {
    description: cmp.helpers.getReportDescription(),
    on: {
      click: () => cmp.helpers.handleEditReportDescription()
    },
    canEdit: true
  }), cmp.helpers.showViewOnlyTextHighlight() ? /* @__PURE__ */ jsx(TextHighlightEl, {
    attrs: {
      elref: `viewOnlyHighlight`,
      markdown: `You have view only access. [Duplicate to make a copy](${cmp.params.duplicateReportUrl}).`,
      theme: THEME.INFORMATION
    }
  }) : null)) : cmp.state.isEditingTitle ? /* @__PURE__ */ jsx(ReportInput, {
    value: cmp.helpers.getReportName(),
    onInputChanged: (value) => cmp.handleReportTitleChanged(value),
    onEditStop: () => cmp.update({ isEditingTitle: false })
  }) : /* @__PURE__ */ jsx(EditDescriptionInput, {
    cmp
  }));
};
const HeaderTitle = ({ cmp }) => {
  const canEdit = cmp.canEditExistingEntity();
  const computedAt = cmp.params.computedAt;
  const description = cmp.params.selectedEntityDescription;
  const samplingNotice = cmp.helpers.getSamplingNotice({ isReportModeEnabled: false });
  const isPublicDashboard = cmp.params.isPublicDashboard || cmp.state.showPublicBadge;
  const isTemplateDashboard = cmp.params.isTemplateDashboard;
  const isDashboardModeEnabled = cmp.isDashboardModeEnabled();
  const InlineEditableTitle = () => {
    return /* @__PURE__ */ jsx("div", {
      sel: selx(styles.mpQueryReportHeaderTitleText),
      attrs: {
        elref: `reportTitleButton`,
        title: cmp.params.selectedEntityName || cmp.params.emptyEntityName
      },
      class: {
        [styles.unsavedBookmark]: !cmp.hasSelectedEntity,
        [styles.editableBookmark]: canEdit
      },
      on: {
        click: canEdit && cmp.helpers.editReportClicked
      }
    }, cmp.helpers.getTitle(), canEdit ? /* @__PURE__ */ jsx(ButtonEl, {
      sel: selx(styles.reportEditButton),
      attrs: {
        icon: `edit`,
        theme: ButtonTheme.Tertiary,
        elref: `reportEditButton`
      },
      on: {
        click: cmp.helpers.editReportClicked
      }
    }) : null);
  };
  return /* @__PURE__ */ jsx("div", {
    class: {
      [styles.dashboardsBoardUi]: isDashboardModeEnabled
    }
  }, /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryReportHeaderTitleTextContainer)
  }, cmp.params.dashboardsRoute ? cmp.state.isEditingDescription ? /* @__PURE__ */ jsx(EditDescriptionInput, {
    cmp
  }) : /* @__PURE__ */ jsx(BreadcrumbsTitle, {
    dashboardsRoute: cmp.params.dashboardsRoute,
    title: cmp.helpers.getTitle(),
    id: cmp.params.selectedEntityId,
    isEditingTitle: cmp.state.isEditingTitle,
    displayContext: cmp.params.displayContext,
    projectId: cmp.params.projectId,
    workspaceId: cmp.params.workspaceId,
    canEdit,
    description,
    isViewOnly: cmp.helpers.showViewOnlyTextHighlight(),
    duplicateReportUrl: cmp.params.duplicateReportUrl,
    onTitleChanged: (value) => {
      cmp.submitSave({ newReportData: { name: value } });
    },
    onDescriptionClicked: () => cmp.helpers.handleEditReportDescription(),
    onTitleClicked: () => cmp.helpers.handleEditReportName()
  }) : /* @__PURE__ */ jsx(InlineEditableTitle, null), isDashboardModeEnabled && cmp.params.boardsTitleBadgeEl), /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryReportHeaderTitleSubtext),
    class: {
      [styles.mpQueryReportHeaderTitleSubtextBoardsDashboard]: isDashboardModeEnabled && !!description
    }
  }, /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryReportHeaderTitleSubtextDescriptionContainer)
  }, isPublicDashboard ? getPublicDashboardTextHighlight(cmp.helpers.handleViewPublicDashboardLinksClick) : null, isTemplateDashboard ? /* @__PURE__ */ jsx(null, null, cmp.helpers.isFreePlan() && /* @__PURE__ */ jsx("div", null, /* @__PURE__ */ jsx(TooltipEl, {
    attrs: {
      elref: `templateDashboardFreePlanTooltip`,
      text: `Your plan includes 5 reports. The reports in this template don't count towards that limit until you modify and save them.`,
      placement: Placement.RightCenter
    }
  }), /* @__PURE__ */ jsx(TextHighlightEl, {
    sel: selx(styles.mpTemplateHighlight),
    attrs: {
      elref: `templateDashboardTextHighlight`,
      text: `Mixpanel Template`,
      size: SIZE.SMALL,
      theme: THEME.NEUTRAL,
      "style-override": `.mp-text-highlight.neutral {font-family: "Apercu Pro", "Helvetica Neue", Helvetica, Tahoma, Geneva, Arial, sans-serif;}`
    }
  }))) : null, description && !cmp.params.dashboardsRoute ? /* @__PURE__ */ jsx(ClampedTextEl, {
    attrs: { text: description }
  }) : null), /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryReportHeaderMetadata)
  }, computedAt ? /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryReportHeaderLastUpdated)
  }, /* @__PURE__ */ jsx(LastUpdatedEl, {
    attrs: {
      "is-compact": false,
      "last-refresh-date": computedAt,
      "preface-text": `Updated`,
      "refresh-interval-ms": cmp.params.autoRefreshIntervalMs
    },
    on: { refresh: cmp.helpers.handleAutoRefresh }
  })) : null, samplingNotice ? /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryReportHeaderSamplingNotice)
  }, samplingNotice) : null)));
};
const HeaderActions = ({ cmp }) => {
  var _a;
  const controlBarActions = /* @__PURE__ */ jsx(ControlBarEl, {
    class: { [styles.controlBarBoardsReport]: cmp.helpers.isReportModeEnabled() && cmp.hasSelectedEntity },
    attrs: cmp.helpers.serializeAttributes({
      theme: `tertiary`,
      controls: cmp.getHeaderActionItems(),
      "overflow-menu-width": 220,
      "overflow-tooltip": { text: cmp.helpers.getHeaderActionOverflowTooltip() },
      ...cmp.helpers.isDashboardModeEnabled() || cmp.helpers.isReportModeEnabled() ? {
        "overflow-menu-height": 500,
        "style-override": `.mp-control-bar .mp-control-bar-control {margin-right: 12px;}`
      } : {}
    }),
    on: {
      select: cmp.helpers.onHeaderActionClick,
      click: cmp.helpers.onOverflowMenuClick
    },
    props: { sectionsFn: () => cmp.getHeaderActionOverflowItems() }
  }, /* @__PURE__ */ jsx(CreatorWithViewsIcon, {
    userListSel: selx(styles.createdBy),
    slot: `createdBy`,
    trackingProps: cmp.getPresenceTrackingProps(),
    entityEditor: cmp.params.selectedEntityEditor,
    entityCreator: cmp.params.selectedEntityCreator,
    presenceViews: cmp.state.presenceViews,
    totalPresenceViews: cmp.state.totalPresenceViews
  }), cmp.helpers.showShareButton() ? /* @__PURE__ */ jsx(SharingStatusButtonEl, {
    attrs: {
      elref: `reportShareButton`,
      disabled: !cmp.helpers.isShareButtonEnabled(),
      "hide-share-button": cmp.helpers.isReportModeEnabled(),
      id: cmp.params.selectedEntityId,
      "is-select": false,
      "project-id": cmp.params.projectId,
      theme: cmp.helpers.getShareButtonTheme(),
      "tooltip-text": !cmp.helpers.isReportModeEnabled() && cmp.helpers.getShareTooltipText(),
      type: cmp.helpers.getSharableEntityType(),
      slot: `sharing`
    },
    props: {
      extraTrackingParams: cmp.extraTrackingParams
    },
    on: {
      share: cmp.helpers.isShareButtonEnabled() ? cmp.helpers.handleSharingStatusButtonClick : null
    }
  }) : null, cmp.params.isQuerySamplingActionVisible ? /* @__PURE__ */ jsx("span", {
    attrs: { slot: `toggle-query-sampling` }
  }, /* @__PURE__ */ jsx(SwitchEl, {
    attrs: {
      disabled: Boolean(cmp.params.querySamplingDisabledMessage),
      on: cmp.params.isQuerySamplingEnabled,
      icon: `sampling`,
      showcase: cmp.params.triggerQuerySamplingTourTooltip,
      "show-icon-while-off": true,
      slot: `toggle-query-sampling`,
      elref: `querySamplingDisabledSwitch`
    },
    on: {
      change: (ev) => cmp.helpers.handleToggleQuerySampling(ev)
    }
  }), cmp.params.triggerQuerySamplingTourTooltip ? /* @__PURE__ */ jsx(TooltipTourEl, {
    params: {
      uniqueTourName: TourName.QueryTimeSampling,
      showDoNotShowCheckbox: true,
      title: `Enable Query Sampling`,
      markdownText: `Shorten the time it takes to load results
              by querying only a subset of data.`,
      primaryButtonLabel: `Got it`,
      secondaryButtonLabel: `Learn More`,
      onPrimaryButtonClick: () => {
        cmp.dispatchEvent(
          new CustomEvent(`actionclick`, { detail: { value: HeaderAction.DismissQuerySamplingTour } })
        );
      },
      onSecondaryButtonBlick: () => window.open(
        `https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#query-time-sampling`,
        `_blank`
      )
    }
  }) : null) : null, cmp.params.duplicateReportUrl ? /* @__PURE__ */ jsx("div", {
    attrs: { slot: `boardsDuplicate` }
  }, /* @__PURE__ */ jsx(TooltipEl, {
    attrs: {
      text: `Duplicate`
    }
  }), /* @__PURE__ */ jsx(ButtonEl, {
    attrs: {
      theme: ButtonTheme.Tertiary,
      elref: `boardsDuplicateButton`,
      href: cmp.params.duplicateReportUrl,
      target: `_blank`,
      icon: `duplicate`
    }
  })) : null, cmp.params.headerActionItemsEl);
  const cancelSaveReportButton = /* @__PURE__ */ jsx("div", {
    sel: selx(styles.boardsHeaderAction)
  }, /* @__PURE__ */ jsx(ButtonEl, {
    attrs: {
      theme: ButtonTheme.Tertiary,
      elref: `boardsCancelButton`,
      href: cmp.helpers.getCloseLink()
    },
    on: { click: cmp.helpers.handleBoardButtonClick }
  }, "Close"));
  const boardsMainDashboardId = (_a = cmp.helpers.getBoardsMainDashboard()) == null ? void 0 : _a.id;
  const saveReportButton = /* @__PURE__ */ jsx("div", {
    sel: selx(styles.boardsHeaderAction)
  }, /* @__PURE__ */ jsx(BoardsSaveButtonEl, {
    attrs: {
      "selected-dashboard-id": boardsMainDashboardId,
      "user-id": cmp.params.userId,
      disabled: cmp.isSaveButtonDisabled(),
      tooltip: !!cmp.isSaveButtonDisabled() && !cmp.state.showBoardsSaveButtonSpinner && cmp.getSaveTooltipText(),
      elref: `boardsSaveButton`,
      "align-right": true,
      "search-placeholder": `Save to board...`,
      loading: cmp.state.showBoardsSaveButtonSpinner,
      "has-unsaved-changes": cmp.hasUnsavedChanges
    },
    props: {
      dashboardController: cmp.dashboardController,
      recentDashboardIds: cmp.getRecentlyViewedDashboards()
    },
    on: {
      menuchange: cmp.helpers.handleBoardsSaveMenuChange,
      save: (ev) => cmp.helpers.boardsSaveInDashboard(
        boardsMainDashboardId || ev.detail.dashboardId,
        cmp.hasSelectedEntity ? SaveModes.Save : SaveModes.SaveNew
      )
    }
  }));
  return /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryReportHeaderActions)
  }, cmp.helpers.isDashboardModeEnabled() ? /* @__PURE__ */ jsx(null, null, controlBarActions) : cmp.helpers.isReportModeEnabled() ? /* @__PURE__ */ jsx("div", {
    sel: selx(styles.boardsExtraActions)
  }, controlBarActions, /* @__PURE__ */ jsx("div", {
    sel: selx(styles.gapLine)
  }), cmp.showAvailableDashboardsDropdown() && cancelSaveReportButton, saveReportButton) : controlBarActions);
};
const SaveWarningModal = ({ cmp }) => /* @__PURE__ */ jsx(ConfirmEl, {
  attrs: {
    theme: confirmTheme.CONFIRM,
    "confirm-button-label": `Save changes`,
    open: cmp.state.showSaveWarningModal,
    closeable: true
  },
  style: { width: `700px` },
  on: {
    submit: cmp.helpers.saveWarningModalSubmitted,
    change: cmp.helpers.saveWarningModalChanged
  }
}, /* @__PURE__ */ jsx("div", null, /* @__PURE__ */ jsx("div", {
  sel: ".mp-confirm-title"
}, "Save Report Changes"), /* @__PURE__ */ jsx("div", {
  sel: ".mp-confirm-description"
}, cmp.helpers.getEditWarning()), /* @__PURE__ */ jsx(TableEl, {
  on: {
    cellClick: cmp.helpers.handleSaveWarningTableCellCliked
  },
  style: { "margin-top": `20px` },
  attrs: {
    "default-sort-field": `name`,
    "empty-message": `You do not have any alerts.`,
    "max-body-height": `300px`,
    "style-override": `.mp-table {border: 1px solid var(--gray1); border-radius: 12px; overflow: hidden;}`
  },
  props: {
    rows: cmp.customAlerts.map((customAlert) => {
      var _a;
      return {
        name: customAlert == null ? void 0 : customAlert.name,
        creator: (_a = customAlert == null ? void 0 : customAlert.creator) == null ? void 0 : _a.name,
        created: moment.utc(customAlert == null ? void 0 : customAlert.created).local()
      };
    }),
    columns: [
      {
        title: `Name`,
        field: `name`,
        isSearchable: true,
        isSortable: true,
        flexGrow: `3`,
        type: `text-link`
      },
      Object.assign(
        {
          title: `Created by`,
          field: `creator`,
          isSearchable: true,
          isSortable: true,
          flexGrow: `4`,
          type: `user`
        },
        userColumnProps
      ),
      {
        field: `created`,
        title: `Created`,
        flexGrow: `3`,
        isSortable: true,
        sortTransform: (row) => row.created.unix(),
        labelTransform: (row) => row.created.format(`MMM D, YYYY`)
      }
    ]
  }
}), /* @__PURE__ */ jsx(ButtonEl, {
  attrs: {
    theme: ButtonTheme.Secondary,
    href: cmp.params.duplicateReportUrl,
    target: `_blank`
  },
  style: { position: `relative`, top: `56px` }
}, "Save as new report")));
export function template() {
  var _a, _b;
  const isReportModeEnabled = this.helpers.isReportModeEnabled();
  const { showSaveWarningModal } = this.state;
  return /* @__PURE__ */ jsx("div", {
    sel: selx(styles.mpQueryReportHeader),
    class: { [styles.boardsMode]: isReportModeEnabled },
    style: { "--header-height": `${REPORT_HEADER_HEIGHT}px` }
  }, this.helpers.isHeaderReady() ? isReportModeEnabled ? /* @__PURE__ */ jsx(null, null, this.params.dashboardsRoute ? this.state.isEditingDescription ? /* @__PURE__ */ jsx(EditDescriptionInput, {
    cmp: this
  }) : /* @__PURE__ */ jsx(BreadcrumbsTitle, {
    dashboardsRoute: this.params.dashboardsRoute,
    title: this.helpers.getTitle(),
    id: this.params.selectedEntityId,
    isEditingTitle: this.state.isEditingTitle,
    displayContext: this.params.displayContext,
    projectId: this.params.projectId,
    workspaceId: this.params.workspaceId,
    canEdit: this.canEditExistingEntity(),
    description: this.helpers.getReportDescription(),
    isViewOnly: this.helpers.showViewOnlyTextHighlight(),
    duplicateReportUrl: this.params.duplicateReportUrl,
    onDescriptionClicked: () => this.helpers.handleEditReportDescription(),
    onTitleChanged: (newTitle) => this.handleReportTitleChanged(newTitle),
    onTitleClicked: () => this.helpers.handleEditReportName()
  }) : /* @__PURE__ */ jsx(null, null, this.showAvailableDashboardsDropdown() && !this.helpers.isEditingReportNameOrDescription() && /* @__PURE__ */ jsx(ButtonEl, {
    sel: selx(styles.boardReturnButton),
    attrs: {
      elref: `boardsDashboardsReturnButton`,
      href: this.helpers.getReturnToBoardLink(),
      icon: `arrow-left`,
      theme: ButtonTheme.Tertiary,
      tooltip: { text: `Back to ${(_a = this.helpers.getBoardsRedirectDashboard()) == null ? void 0 : _a.title}` }
    },
    on: { click: this.helpers.handleBoardButtonClick }
  }, /* @__PURE__ */ jsx("div", {
    sel: selx(styles.dashboardTitle),
    attrs: { elref: `boardsDashboardTitle` }
  }, truncate((_b = this.helpers.getBoardsRedirectDashboard()) == null ? void 0 : _b.title, 30))), /* @__PURE__ */ jsx(BoardsTitle, {
    cmp: this
  })), /* @__PURE__ */ jsx(HeaderActions, {
    cmp: this
  })) : /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(HeaderTitle, {
    cmp: this
  }), /* @__PURE__ */ jsx(HeaderActions, {
    cmp: this
  })) : null, /* @__PURE__ */ jsx(AlertModal, {
    cmp: this
  }), showSaveWarningModal ? /* @__PURE__ */ jsx(SaveWarningModal, {
    cmp: this
  }) : null);
}
